import { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { DocumentModel } from '@ctw/shared/api/fhir/models/document';
import { MedicationStatementModel } from '@ctw/shared/api/fhir/models/medication-statement';
import { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import { Alert } from '@ctw/shared/components/alert';
import { Button } from '@ctw/shared/components/button';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { Checkbox } from '@ctw/shared/components/form/checkbox';
import { MainContentWithDockableDrawer } from '@ctw/shared/components/layout/main-content-with-dockable-drawer';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { SortButton } from '@ctw/shared/components/sort-button/sort-button';
import { SearchResultRow } from '@ctw/shared/content/patient-record-search/search-result-row';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type PatientRecordSearchResult,
  usePatientRecordSearch,
} from '@ctw/shared/hooks/use-patient-record-search';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faSearch, faX } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SetRequired } from 'type-fest';

const GlobalModalStyleOverrides = () => {
  // Work around the fact that our modals are not designed to contain drawers and style
  // the modal containers directly. I don't feel good about this, but we're redesigning them soon
  // after which this can be ripped out.
  const css = `
    [data-testid="modal-wrapper"] {
      height: auto !important;
    }
    
    [data-testid="modal-content-container"] {
      overflow: hidden !important;
      height: 100% !important;
    }
    
    [data-testid="modal"] {
      overflow: hidden !important;
      height: 100% !important;
    }
  `;

  return <style>{css}</style>;
};

const SEARCH_SORT_OPTIONS: Array<string> = ['relevance', 'date (desc)'] as const;
type SearchSortOptions = (typeof SEARCH_SORT_OPTIONS)[number];

const SETTLE_DELAY_MS = 5000;

export const PatientRecordSearch = withErrorBoundary({
  boundaryName: 'PatientRecordSearch',
  Component: () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isExactMatch, setIsExactMatch] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const settledTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const {
      data: searchResults,
      isLoading,
      isError,
    } = usePatientRecordSearch(searchQuery, isExactMatch);
    const { trackInteraction } = useTelemetry();

    const handleSearchSubmit = useCallback(
      (query: string) => {
        const trimmedQuery = query.trim();
        setSearchQuery(trimmedQuery);
        trackInteraction('patient_record_search_submit');

        if (settledTimerRef.current) {
          clearTimeout(settledTimerRef.current);
          settledTimerRef.current = null;
        }

        if (!isEmpty(trimmedQuery)) {
          settledTimerRef.current = setTimeout(() => {
            settledTimerRef.current = null;
            trackInteraction('patient_record_search_settled');
          }, SETTLE_DELAY_MS);
        }
      },
      [trackInteraction],
    );

    // Delay handle search input so that we don't fire a bunch of events until the user has had time to type.
    const debouncedHandleSearchSubmit = useMemo(
      () => debounce(handleSearchSubmit, 300),
      [handleSearchSubmit],
    );

    const clearSearch = useCallback(() => {
      if (settledTimerRef.current) {
        clearTimeout(settledTimerRef.current);
        settledTimerRef.current = null;
      }
      trackInteraction('patient_record_search_clear');
      setSearchQuery('');

      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.value = '';
      }
    }, [trackInteraction]);

    const searchReturnedNoResults = useMemo(
      () =>
        !(isLoading || isError || isEmpty(searchQuery)) &&
        (searchResults === undefined || searchResults.length === 0),
      [searchQuery, searchResults, isLoading, isError],
    );

    useEffect(() => {
      // Auto-focus the input when the modal opens
      inputRef.current?.focus();
      return () => {
        if (settledTimerRef.current) {
          clearTimeout(settledTimerRef.current);
          settledTimerRef.current = null;
        }
      };
    }, []);

    return (
      <div
        className={twx('flex min-h-full w-[75vw] max-w-[1200px] flex-col p-2', {
          '[@media(max-height:664px)]:h-[90vh] [@media(min-height:665px)]:h-[65vh]':
            searchResults && searchResults.length > 0,
        })}
      >
        <GlobalModalStyleOverrides />
        <div className={tw`z-navigation-sticky bg-white`}>
          <div className={tw`relative`}>
            <div
              className={tw`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3`}
            >
              <FontAwesomeIcon icon={faSearch} className={tw`h-5 w-5 text-content-icon`} />
            </div>
            <input
              ref={inputRef}
              type="text"
              className={tw`w-full overflow-hidden overflow-ellipsis whitespace-nowrap rounded-md border border-content-icon border-solid bg-white px-3 py-2 pr-8 pl-10 text-sm shadow-sm`}
              placeholder="Search the ZAP"
              name="patientRecordSearch"
              onChange={() => {
                if (inputRef.current) {
                  debouncedHandleSearchSubmit(inputRef.current.value);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();

                  if (inputRef.current) {
                    handleSearchSubmit(inputRef.current.value);
                  }
                }
              }}
            />
            <Button
              aria-label="Clear search"
              type="button"
              onClick={clearSearch}
              variant="unstyled"
              className={tw`absolute top-0 right-2 bottom-0 flex cursor-pointer items-center border-0 bg-transparent p-1.5 text-primary-text leading-none outline-none hover:text-primary-main`}
            >
              <FontAwesomeIcon icon={faX} className={tw`h-3 w-3 text-content-icon`} />
            </Button>
          </div>
        </div>
        {searchReturnedNoResults && (
          <span className={tw`block py-4 text-center font-medium text-1xl text-content-dark`}>
            Search did not find any results.
          </span>
        )}
        {!(isEmpty(searchQuery) || searchReturnedNoResults) && (
          <MainContentWithDockableDrawer
            key={searchQuery}
            dockingMode="always"
            dockDisplay="detail"
            fullbleed={true}
            modalDeepLinkBehavior="off"
            drawerDeepLinkBehavior="off"
            mainContentHiddenBelow="lg"
            contentContainerClassName={tw`min-h-0 h-full min-w-full max-w-full`}
            dockableDrawerContainerClassName={twx(
              // Work around the fact that our drawers are not designed to be rendered in modals and
              // style the drawer containers via Tailwind child selectors. I don't feel good about
              // this, but we're redesigning them soon after which this can be ripped out.
              '[&_[data-testid="drawer-body"]]:!p-0',
            )}
            onResourceDrawerFirstViewBackButtonClick={({ drawer }) => drawer.closeDrawer()}
          >
            <PatientRecordContent
              searchResults={searchResults ?? []}
              isLoading={isLoading}
              isError={isError}
              isExactMatch={isExactMatch}
              setIsExactMatch={setIsExactMatch}
            />
          </MainContentWithDockableDrawer>
        )}
      </div>
    );
  },
});

interface PatientRecordSearchResultsProps {
  searchResults: NonNullable<ReturnType<typeof usePatientRecordSearch>['data']>;
  isLoading: boolean;
  isError: boolean;
  isExactMatch: boolean;
  setIsExactMatch: (isExactMatch: boolean) => void;
}

const PatientRecordContent = ({
  searchResults,
  isLoading,
  isError,
  isExactMatch,
  setIsExactMatch,
}: PatientRecordSearchResultsProps) => {
  const [sortBy, setSortBy] = useState<string>('relevance');

  const sortedResults = useMemo(() => {
    if (sortBy === 'relevance') {
      return searchResults;
    }
    return [...searchResults].sort((a, b) => {
      const getSortableDates = (result: PatientRecordSearchResult) => {
        const resource = result.resource;
        const dates: Array<string | undefined> = [];
        if (resource instanceof AllergyModel) {
          dates.push(resource.onset);
        } else if (resource instanceof ConditionModel) {
          dates.push(resource.recordedDate);
        } else if (resource instanceof DiagnosticReportModel) {
          dates.push(resource.date);
        } else if (resource instanceof DocumentModel) {
          dates.push(resource.contextPeriodStartDate);
          dates.push(resource.contextPeriodEndDate);
        } else if (resource instanceof MedicationStatementModel) {
          dates.push(resource.dateAsserted);
        } else if (resource instanceof ObservationModel) {
          dates.push(resource.date);
        }
        return dates.map((date) => (date ? new Date(date).getTime() : 0));
      };

      const dateA = getSortableDates(a);
      const dateB = getSortableDates(b);

      // Utilize tiebreakers
      for (let i = 0; i < dateA.length; i++) {
        const aVal = dateA[i];
        const bVal = dateB[i];
        if (bVal !== aVal) {
          return bVal - aVal;
        }
      }
      return 0;
    });
  }, [searchResults, sortBy]);

  return (
    <div className={tw`relative mx-auto h-full w-full items-center text-center text-base`}>
      {isLoading ? (
        <LoadingSpinner message="Loading..." className={tw`mx-auto py-5`} />
      ) : (
        <div className={tw`m-0 ml-0 space-y-2.5`}>
          <div className={tw`ml-0 w-full text-center align-left`}>
            {isError && (
              <div className={tw`w-full`}>
                <Alert type="error" header="Error">
                  There was an error running your search.
                </Alert>
              </div>
            )}
            <div className={tw`mb-1 flex flex-col space-y-2 py-2`}>
              <div className={tw`flex items-center text-left font-xs text-content-subtle`}>
                {searchResults.length === 500 ? 'Top' : 'Found'} {searchResults.length} results.
              </div>
              <div className={tw`flex flex-row gap-4 self-start`}>
                <SortButton<SearchSortOptions>
                  defaultSort="relevance"
                  options={SEARCH_SORT_OPTIONS}
                  onChange={setSortBy}
                />
                <Checkbox
                  name="patientRecordSearchExactMatch"
                  label="Exact match only"
                  checked={isExactMatch}
                  onChange={setIsExactMatch}
                  className={tw`text-content-subtle`}
                />
              </div>
            </div>
            <div className={tw`flex flex-col gap-2 pr-2`}>
              {sortedResults.map((result) => (
                <SearchResultRow
                  key={result.id}
                  searchResult={result as SetRequired<PatientRecordSearchResult, 'resource'>}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
